import React from "react";
import { ScrollView, StyleSheet } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { View } from "../components/Themed";
import LegalDocument from "../components/LegalDocument";
import PublicFooter from "../components/PublicFooter";
import Config from "../constants/Config";
import i18n from "../utils/localization";

function buildBlocks(legal) {
  return [
    { type: "p", text: i18n.t("These Terms & Conditions (\u201CTerms\u201D) govern your use of the website, mobile application, on-premise services, and online ordering platform (collectively, the \u201CServices\u201D) operated under the brand {tradingAs} by {companyName}, registered address {address}, Tax ID ({taxIdLabel}) {taxId}, email {email}.")
      .replace("{tradingAs}", legal.tradingAs)
      .replace("{companyName}", legal.companyName)
      .replace("{address}", legal.address)
      .replace("{taxIdLabel}", legal.taxIdLabel)
      .replace("{taxId}", legal.taxId)
      .replace("{email}", legal.email) },
    { type: "p", text: i18n.t("In these Terms, \u201Cwe\u201D, \u201Cus\u201D, \u201Cour\u201D, and \u201Cthe Company\u201D refer to {companyName}. \u201CYou\u201D or \u201Cthe User\u201D refers to any person accessing or using the Services.")
      .replace("{companyName}", legal.companyName) },

    { type: "h2", text: i18n.t("1. About us") },
    { type: "p", text: i18n.t("{companyName} is a Bulgarian company operating the {tradingAs} venue and ordering platform. All correspondence regarding these Terms or our Services may be addressed to the contact details above.")
      .replace("{companyName}", legal.companyName)
      .replace("{tradingAs}", legal.tradingAs) },

    { type: "h2", text: i18n.t("2. Scope of services") },
    { type: "p", text: i18n.t("The Services include, without limitation:") },
    { type: "list", items: [
      i18n.t("On-premise food & beverage service at our venue(s), including dine-in service, bar service, and self-service ordering via QR code at the table."),
      i18n.t("Online ordering for pickup placed through our website or app and collected by you at the venue."),
      i18n.t("Online ordering for delivery to an address you specify within our delivery zones, fulfilled by us or by partner couriers."),
      i18n.t("Gaming, VR, and esports modules — optional entertainment services available on-premise, either standalone or as part of a booking."),
    ]},
    { type: "p", text: i18n.t("Availability of menu items, delivery zones, opening hours, and gaming modules may change at any time without prior notice.") },

    { type: "h2", text: i18n.t("3. Acceptance of terms and changes") },
    { type: "p", text: i18n.t("By accessing the Services, placing an order, scanning a QR menu, creating an account, or entering our venue, you confirm that you have read, understood, and agreed to these Terms.") },
    { type: "p", text: i18n.t("We may update these Terms from time to time to reflect changes in our Services, legal requirements, or business practices. The current version is always available on our website and in the app. Material changes will be communicated through the app or by email where reasonably possible. Continued use of the Services after changes take effect constitutes acceptance of the revised Terms.") },

    { type: "h2", text: i18n.t("4. User accounts and age requirements") },
    { type: "p", text: i18n.t("You may browse menus and place certain orders as a guest, but some features (order history, saved addresses, loyalty, bookings) require an account.") },
    { type: "p", text: i18n.t("When creating an account, you agree to:") },
    { type: "list", items: [
      i18n.t("Provide accurate, current, and complete information."),
      i18n.t("Keep your login credentials confidential and not share your account."),
      i18n.t("Be responsible for all activity that occurs under your account."),
      i18n.t("Notify us immediately of any unauthorized use."),
    ]},
    { type: "h3", text: i18n.t("Age requirements") },
    { type: "p", text: i18n.t("You must be at least 18 years old to order, purchase, or be served alcoholic beverages or tobacco products, in accordance with Bulgarian law. Our staff and couriers may request a valid photo ID at the point of service or delivery and may refuse service if age cannot be verified.") },
    { type: "p", text: i18n.t("Certain gaming, VR, or esports modules may have minimum age requirements or content ratings (e.g. PEGI). These will be indicated at the point of booking. Minors may be required to be accompanied by a parent or guardian.") },
    { type: "p", text: i18n.t("We reserve the right to suspend or terminate accounts that breach these Terms or applicable law.") },

    { type: "h2", text: i18n.t("5. Ordering process, acceptance, and refusal") },
    { type: "p", text: i18n.t("An order placed through the Services constitutes an offer by you to purchase the selected items. A contract is formed only when we confirm acceptance of your order — typically by an in-app or email confirmation, or by the venue accepting your QR-table order.") },
    { type: "p", text: i18n.t("We reserve the right to refuse, cancel, or modify any order, including without limitation where:") },
    { type: "list", items: [
      i18n.t("An item is out of stock or unavailable."),
      i18n.t("There is an obvious pricing, description, or stock error."),
      i18n.t("Delivery to your address cannot reasonably be performed."),
      i18n.t("We suspect fraud, abuse, or breach of these Terms."),
      i18n.t("Age verification fails for age-restricted items."),
    ]},
    { type: "p", text: i18n.t("You may request modification or cancellation of an order before it has been accepted into preparation. Once preparation begins, orders generally cannot be cancelled, as food and drink are perishable goods prepared to order and are exempt from the standard 14-day withdrawal right under the Bulgarian Consumer Protection Act and Directive 2011/83/EU.") },
    { type: "p", text: i18n.t("If an item is defective, incorrect, or not of satisfactory quality, please contact us promptly and we will offer a replacement, refund, or other appropriate remedy in line with your statutory rights.") },

    { type: "h2", text: i18n.t("6. Pricing, taxes, and currency") },
    { type: "p", text: i18n.t("Prices are displayed in Bulgarian Lev (BGN) and, where applicable, in Euro (EUR). Prices include VAT at the rate applicable under Bulgarian law. Delivery fees, service fees, and packaging charges (where applicable) are shown separately before you confirm your order.") },
    { type: "p", text: i18n.t("We strive to keep all pricing accurate but reserve the right to correct errors and adjust prices at any time. If a clear pricing error is discovered after order placement, we will contact you to confirm or cancel the order before fulfillment.") },

    { type: "h2", text: i18n.t("7. Payment") },
    { type: "p", text: i18n.t("We accept the following payment methods, subject to availability:") },
    { type: "list", items: [
      i18n.t("Pay at pickup — cash or card at our venue when collecting your order."),
      i18n.t("Pay on delivery — cash or card with the courier upon delivery."),
      i18n.t("Online card payment via Stripe."),
      i18n.t("Online card payment via myPOS."),
    ]},
    { type: "p", text: i18n.t("Online card payments are processed by our third-party payment providers under their own terms and security standards (PCI-DSS). We do not store your full card details. You are responsible for ensuring you are authorized to use the payment method submitted.") },
    { type: "p", text: i18n.t("A fiscal receipt or e-receipt will be issued in accordance with Bulgarian tax law.") },

    { type: "h2", text: i18n.t("8. Intellectual property") },
    { type: "p", text: i18n.t("All content available through the Services — including the {tradingAs} and {companyName} names and logos, menus, recipes, photography, graphics, software, user interface, and text — is owned by or licensed to {companyName} and is protected by Bulgarian, EU, and international intellectual property laws.")
      .replace("{tradingAs}", legal.tradingAs)
      .replace("{companyName}", legal.companyName)
      .replace("{companyName}", legal.companyName) },
    { type: "p", text: i18n.t("You are granted a limited, non-exclusive, non-transferable, revocable license to use the Services for personal, non-commercial purposes. You may not copy, reproduce, modify, distribute, scrape, reverse engineer, or create derivative works without our prior written consent.") },

    { type: "h2", text: i18n.t("9. Acceptable use") },
    { type: "p", text: i18n.t("When using the Services or visiting our venue, you agree to:") },
    { type: "list", items: [
      i18n.t("Behave respectfully toward staff, couriers, and other guests."),
      i18n.t("Not engage in violent, harassing, discriminatory, or unlawful conduct."),
      i18n.t("Not bring outside food or beverages without permission."),
      i18n.t("Comply with venue rules, including those regarding smoking, dress code, animals, capacity, and use of gaming/VR equipment."),
      i18n.t("Use gaming and VR equipment only as instructed; you are responsible for any damage caused by misuse."),
      i18n.t("Not place fraudulent orders or chargebacks without legitimate cause."),
    ]},
    { type: "p", text: i18n.t("We reserve the right to refuse entry or service, and to remove any person from the venue, in compliance with Bulgarian law.") },

    { type: "h2", text: i18n.t("10. Limitation of liability") },
    { type: "p", text: i18n.t("To the maximum extent permitted by Bulgarian law:") },
    { type: "list", items: [
      i18n.t("The Services are provided \u201Cas available\u201D without warranties beyond those implied by law."),
      i18n.t("We are not liable for indirect, incidental, consequential, or punitive damages, lost profits, or loss of data arising from your use of the Services."),
      i18n.t("Our aggregate liability for any claim related to a specific order shall not exceed the amount you paid for that order."),
      i18n.t("We are not liable for delays or failures caused by events beyond our reasonable control (force majeure), including utility outages, strikes, supplier failures, traffic, weather, or governmental measures."),
      i18n.t("Nothing in these Terms limits liability that cannot be excluded under Bulgarian law, including liability for death, personal injury caused by negligence, or fraud."),
    ]},
    { type: "p", text: i18n.t("You are responsible for informing us of food allergies or intolerances before ordering. While we take reasonable care, our kitchens handle common allergens and cross-contamination cannot be fully excluded.") },

    { type: "h2", text: i18n.t("11. Governing law and dispute resolution") },
    { type: "p", text: i18n.t("These Terms and any dispute arising from or in connection with them are governed by the laws of the {governingLaw}, without prejudice to mandatory consumer protections available to you under EU law.")
      .replace("{governingLaw}", legal.governingLaw) },
    { type: "p", text: i18n.t("We aim to resolve any complaint amicably. Please contact us first at {email} so we can address your concerns. If a dispute cannot be resolved directly, you may contact:")
      .replace("{email}", legal.email) },
    { type: "list", items: [
      i18n.t("{consumerAuthorityName} — {consumerAuthorityUrl}")
        .replace("{consumerAuthorityName}", legal.consumerAuthority.name)
        .replace("{consumerAuthorityUrl}", legal.consumerAuthority.url),
      i18n.t("EU Online Dispute Resolution (ODR) platform — {odrUrl}")
        .replace("{odrUrl}", legal.odrUrl),
    ]},
    { type: "p", text: i18n.t("The competent courts of {serviceCity}, {country} shall have jurisdiction, subject to any mandatory consumer jurisdiction rules that allow you to bring proceedings in your country of residence.")
      .replace("{serviceCity}", legal.serviceCity)
      .replace("{country}", legal.country) },

    { type: "h2", text: i18n.t("12. Contact") },
    { type: "p", text: i18n.t("{companyName} ({tradingAs})\n{address}\n{taxIdLabel}: {taxId}\nEmail: {email}\nPhone: {phone}")
      .replace("{companyName}", legal.companyName)
      .replace("{tradingAs}", legal.tradingAs)
      .replace("{address}", legal.address)
      .replace("{taxIdLabel}", legal.taxIdLabel)
      .replace("{taxId}", legal.taxId)
      .replace("{email}", legal.email)
      .replace("{phone}", legal.phone) },
    { type: "p", text: i18n.t("For order-specific issues, please use the in-app support channel or reply to your order confirmation email so we can locate your order quickly.") },
  ];
}

export default function LegalTermsScreen() {
  const legal = Config.legal || {};
  const blocks = buildBlocks(legal);
  return (
    <SafeAreaView style={styles.screen}>
      <ScrollView contentContainerStyle={styles.container}>
        <View style={styles.body}>
          <LegalDocument
            title={i18n.t("Terms & conditions")}
            effective={i18n.t("Last updated: 26 May 2026")}
            blocks={blocks}
          />
        </View>
        <PublicFooter />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: "#ffffff",
  },
  container: {
    flexGrow: 1,
  },
  body: {
    padding: 20,
    maxWidth: 720,
    width: "100%",
    alignSelf: "center",
  },
});
